import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useTRPC } from "@/integrations/trpc/react";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute(
	"/vendor/$buyerSlug/bids/new/$solicitationId",
)({
	component: NewBidRedirectPage,
});

/**
 * This page creates a draft bid (or finds an existing one) and redirects to the edit page.
 * There is no "new bid" form - vendors always work on an actual bid record.
 */
function NewBidRedirectPage() {
	const { solicitationId, buyerSlug } = Route.useParams();
	const navigate = useNavigate();
	const trpc = useTRPC();
	const { user } = useAuth();
	const vendorEmail = user?.email ?? "";
	const buyer = useBuyerContext();
	const hasTriggered = useRef(false);

	// Query solicitation data to get org IDs
	const { data: solicitationData, isLoading: solicitationLoading } = useQuery(
		trpc.public.solicitations.get.queryOptions({
			solicitationId,
		}),
	);

	// Create or get draft bid mutation
	const getOrCreateMutation = useMutation(
		trpc.vendorPortal.bids.getOrCreateDraft.mutationOptions({
			onSuccess: (data) => {
				// Navigate to the bid edit page
				navigate({
					to: "/vendor/$buyerSlug/bids/$bidId/edit",
					params: { buyerSlug, bidId: data.id },
					replace: true,
				});
			},
			onError: (error) => {
				toast.error(`Failed to start bid: ${error.message}`);
				// Navigate back to solicitations list
				navigate({
					to: "/vendor/$buyerSlug/solicitations",
					params: { buyerSlug },
					replace: true,
				});
			},
		}),
	);

	// Trigger the mutation once we have the solicitation data
	useEffect(() => {
		if (
			!solicitationLoading &&
			solicitationData &&
			vendorEmail &&
			!hasTriggered.current &&
			!getOrCreateMutation.isPending
		) {
			hasTriggered.current = true;
			getOrCreateMutation.mutate({
				solicitationId,
				vendorEmail,
				buyerOrgId: solicitationData.buyer_org_id ?? null,
				memberOrgId: solicitationData.member_org_id ?? null,
				subtierOrgId: solicitationData.subtier_org_id ?? null,
			});
		}
	}, [
		solicitationLoading,
		solicitationData,
		vendorEmail,
		solicitationId,
		getOrCreateMutation,
	]);

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="flex flex-col items-center justify-center py-12 gap-4">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				<p className="text-muted-foreground">Starting your bid...</p>
			</div>
		</VendorLayout>
	);
}
